import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { formatDateTimeIST } from '@/utils/dateUtils';
import {
  ClipboardCheck, Clock, FileWarning, ShieldAlert,
  CheckCircle2, XCircle, Eye, MapPin, Camera, AlertTriangle
} from 'lucide-react';

// ====== Tab 1: Missed Checkouts ======
function MissedCheckoutsTab({ onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/attendance/pending-verifications');
      const data = res.data || [];
      setItems(data);
      setSelected(new Set());
      onCountChange?.(data.length);
    } catch (err) {
      toast.error('Failed to load missed checkouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(v => v.id)));
    }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await axiosInstance.put('/attendance/verifications/bulk-approve', { ids: [...selected] });
      toast.success(`${res.data.approved_count} verifications approved`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axiosInstance.put(`/attendance/verifications/${id}/approve`);
      toast.success('Missed checkout approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Enter rejection reason (this will add a strike):');
    if (!reason) return;
    try {
      await axiosInstance.put(`/attendance/verifications/${id}/reject`, { reason });
      toast.success('Verification rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (items.length === 0) return <div className="py-8 text-center text-gray-500">No pending missed checkouts</div>;

  return (
    <div className="space-y-3">
      {/* Bulk action bar */}
      <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 border">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={selected.size === items.length}
            onChange={toggleAll}
            className="rounded border-gray-300 h-4 w-4"
          />
          <span className="font-medium">
            {selected.size > 0 ? `${selected.size} of ${items.length} selected` : `Select all ${items.length} items`}
          </span>
        </label>
        {selected.size > 0 && (
          <Button size="sm" onClick={handleBulkApprove} disabled={bulkLoading} className="bg-green-600 hover:bg-green-700">
            <CheckCircle2 className="h-4 w-4 mr-1" />
            {bulkLoading ? 'Approving...' : `Approve ${selected.size} Selected`}
          </Button>
        )}
      </div>

      {items.map((v) => (
        <Card key={v.id} className={`border-red-200 ${selected.has(v.id) ? 'ring-2 ring-green-400 bg-green-50/30' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selected.has(v.id)}
                onChange={() => toggleSelect(v.id)}
                className="mt-1 rounded border-gray-300 h-4 w-4 cursor-pointer"
              />
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-lg">{v.user_name}</div>
                    <div className="text-sm text-gray-600">{v.date} &middot; {v.shift_time}</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1"><MapPin className="h-3 w-3" />{v.location_name}</div>
                    {v.reason && <div className="mt-2 text-sm bg-gray-50 rounded p-2"><span className="font-medium">Reason:</span> {v.reason}</div>}
                  </div>
                  <div className="text-xs text-gray-400">{formatDateTimeIST(v.created_at) || '-'}</div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => handleApprove(v.id)} className="bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleReject(v.id)}>
                    <XCircle className="h-4 w-4 mr-1" /> Reject (+ Strike)
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ====== Tab 2: Early Checkouts ======
function EarlyCheckoutsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/attendance/early-checkout-requests');
      setItems(res.data || []);
    } catch (err) {
      toast.error('Failed to load early checkout requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (id) => {
    try {
      await axiosInstance.put(`/attendance/early-checkout-requests/${id}/approve`);
      toast.success('Early checkout approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await axiosInstance.put(`/attendance/early-checkout-requests/${id}/reject`);
      toast.success('Early checkout rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (items.length === 0) return <div className="py-8 text-center text-gray-500">No pending early checkout requests</div>;

  return (
    <div className="space-y-3">
      {items.map((r) => (
        <Card key={r.id} className="border-amber-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-semibold text-lg">{r.user_name}</div>
                <div className="text-sm text-gray-600">Shift: {r.shift_id?.slice(0,8)}...</div>
                <div className="mt-2 text-sm bg-amber-50 rounded p-2"><span className="font-medium">Reason:</span> {r.reason}</div>
              </div>
              <div className="text-xs text-gray-400">{formatDateTimeIST(r.created_at) || '-'}</div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={() => handleApprove(r.id)} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)}>
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ====== Tab 3: Regularization Requests ======
function RegularizationTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/attendance/regularization-requests', { params: { status_filter: 'pending' } });
      setItems(res.data || []);
    } catch (err) {
      toast.error('Failed to load regularization requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (id) => {
    try {
      await axiosInstance.put(`/attendance/regularization-requests/${id}/approve`);
      toast.success('Regularization approved \u2014 attendance record created');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await axiosInstance.put(`/attendance/regularization-requests/${id}/reject`, { reason });
      toast.success('Regularization rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const reasonLabels = {
    forgot_checkin: 'Forgot to Check In',
    gps_issue: 'GPS / Location Issue',
    network_issue: 'Network Issue',
    phone_issue: 'Phone Issue',
    other: 'Other'
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (items.length === 0) return <div className="py-8 text-center text-gray-500">No pending regularization requests</div>;

  return (
    <>
      <div className="space-y-3">
        {items.map((r) => (
          <Card key={r.id} className="border-blue-200">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">{r.user_name}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{r.id}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Date: <span className="font-medium">{r.date}</span></div>
                  <div className="text-sm text-gray-600">Claimed: {r.claimed_check_in} &mdash; {r.claimed_check_out}</div>
                  <div className="mt-2 text-sm bg-blue-50 rounded p-2">
                    <span className="font-medium">Reason:</span> {reasonLabels[r.reason_category] || r.reason_category}
                    {r.reason_detail && <span className="text-gray-600"> &mdash; {r.reason_detail}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-xs text-gray-400">{formatDateTimeIST(r.created_at) || '-'}</div>
                  {r.proof_url && (
                    <Button variant="outline" size="sm" onClick={() => setPreviewPhoto(r.proof_url)}>
                      <Eye className="h-4 w-4 mr-1" /> View Proof
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => handleApprove(r.id)} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)}>
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPreviewPhoto(null)}>
          <div className="relative max-w-2xl max-h-[80vh]">
            <img src={previewPhoto} alt="Proof" className="max-w-full max-h-[80vh] rounded-lg" />
            <button className="absolute top-2 right-2 bg-white rounded-full p-2 hover:bg-gray-100" onClick={() => setPreviewPhoto(null)}>&times;</button>
          </div>
        </div>
      )}
    </>
  );
}

// ====== Tab 4: Flagged Records ======
function FlaggedRecordsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/attendance/flagged-records');
      setItems(res.data || []);
    } catch (err) {
      toast.error('Failed to load flagged records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReview = async (id, action) => {
    const notes = action === 'warning_issued' ? prompt('Enter warning notes:') : '';
    try {
      await axiosInstance.put(`/attendance/flagged-records/${id}/review`, { action, notes: notes || '' });
      toast.success('Record reviewed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review');
    }
  };

  const flagLabels = {
    poor_gps_accuracy: 'Poor GPS',
    outside_geofence_warning: 'Outside Geofence',
    outside_geofence: 'Far from Location',
    both: 'GPS + Geofence',
    photo_fallback: 'No Photo',
    gps_fallback: 'Manual GPS',
    missed_checkout_late: 'Late Missed Checkout',
    missed_checkout_abandoned: 'Abandoned Checkout'
  };

  const flagColors = {
    poor_gps_accuracy: 'bg-yellow-100 text-yellow-800',
    outside_geofence_warning: 'bg-orange-100 text-orange-800',
    outside_geofence: 'bg-red-100 text-red-800',
    both: 'bg-red-100 text-red-800',
    photo_fallback: 'bg-purple-100 text-purple-800',
    gps_fallback: 'bg-blue-100 text-blue-800',
    missed_checkout_late: 'bg-amber-100 text-amber-800',
    missed_checkout_abandoned: 'bg-red-100 text-red-800'
  };

  // Handle compound flag reasons like "outside_geofence,poor_gps_accuracy"
  const getFlagLabel = (reason) => {
    if (!reason) return 'Unknown';
    if (flagLabels[reason]) return flagLabels[reason];
    // Compound: split on comma and resolve each part
    const parts = reason.split(',').map(p => flagLabels[p.trim()] || p.trim());
    return parts.join(' + ');
  };
  const getFlagColor = (reason) => {
    if (!reason) return 'bg-gray-100 text-gray-800';
    if (flagColors[reason]) return flagColors[reason];
    // For compound flags, use the most severe color (red)
    return 'bg-red-100 text-red-800';
  };

  if (loading) return <div className="py-8 text-center text-gray-500">Loading...</div>;
  if (items.length === 0) return <div className="py-8 text-center text-gray-500">No flagged records pending review</div>;

  return (
    <div className="space-y-3">
      {items.map((r) => (
        <Card key={r.id} className="border-yellow-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-semibold text-lg">{r.user_name}</div>
                <div className="text-sm text-gray-600">{r.date} &middot; {r.location_name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getFlagColor(r.flag_reason)}`}>
                    {getFlagLabel(r.flag_reason)}
                  </span>
                  {r.check_in_distance_meters && (
                    <span className="text-xs text-gray-500">{Math.round(r.check_in_distance_meters)}m from location</span>
                  )}
                </div>
                {r.photo_fallback_reason && (
                  <div className="mt-2 text-sm bg-purple-50 rounded p-2">
                    <span className="font-medium">Photo fallback reason:</span> {r.photo_fallback_reason}
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-400">{formatDateTimeIST(r.created_at) || '-'}</div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => handleReview(r.id, 'acknowledged')}>
                Acknowledge
              </Button>
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50" onClick={() => handleReview(r.id, 'warning_issued')}>
                <AlertTriangle className="h-4 w-4 mr-1" /> Issue Warning
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleReview(r.id, 'no_action')}>
                No Action
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ====== Count Badge ======
function CountBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white min-w-[20px]">
      {count}
    </span>
  );
}

// ====== Main Page ======
export default function AttendanceVerifications() {
  const [counts, setCounts] = useState({ missed: 0, early: 0, regularization: 0, flagged: 0 });

  // Fetch all counts on mount for badge display
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [missed, early, reg, flagged] = await Promise.allSettled([
          axiosInstance.get('/attendance/pending-verifications'),
          axiosInstance.get('/attendance/early-checkout-requests'),
          axiosInstance.get('/attendance/regularization-requests', { params: { status_filter: 'pending' } }),
          axiosInstance.get('/attendance/flagged-records'),
        ]);
        setCounts({
          missed: missed.status === 'fulfilled' ? (missed.value.data?.length || 0) : 0,
          early: early.status === 'fulfilled' ? (early.value.data?.length || 0) : 0,
          regularization: reg.status === 'fulfilled' ? (reg.value.data?.length || 0) : 0,
          flagged: flagged.status === 'fulfilled' ? (flagged.value.data?.length || 0) : 0,
        });
      } catch { /* non-critical */ }
    };
    fetchCounts();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6 text-[#891d33]" />
          Attendance Verifications
        </h1>
        <p className="text-gray-500 text-sm mt-1">Review and manage attendance exceptions</p>
      </div>

      <Tabs defaultValue="missed" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="missed" className="flex items-center gap-1">
            <ShieldAlert className="h-4 w-4" /> Missed <CountBadge count={counts.missed} />
          </TabsTrigger>
          <TabsTrigger value="early" className="flex items-center gap-1">
            <Clock className="h-4 w-4" /> Early Out <CountBadge count={counts.early} />
          </TabsTrigger>
          <TabsTrigger value="regularization" className="flex items-center gap-1">
            <FileWarning className="h-4 w-4" /> Regularize <CountBadge count={counts.regularization} />
          </TabsTrigger>
          <TabsTrigger value="flagged" className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" /> Flagged <CountBadge count={counts.flagged} />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="missed">
          <MissedCheckoutsTab onCountChange={(c) => setCounts(prev => ({ ...prev, missed: c }))} />
        </TabsContent>
        <TabsContent value="early"><EarlyCheckoutsTab /></TabsContent>
        <TabsContent value="regularization"><RegularizationTab /></TabsContent>
        <TabsContent value="flagged"><FlaggedRecordsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
